import { useEffect, useState } from "react";
import { lastCompany } from "../companies/useMe";
import { Link } from "react-router-dom";
import { sb, signOutEverywhere, usesAccounts } from "../lib/supabase";
import { accountApi, type BillingInvoice, type Plan } from "../lib/api";
import { useLoad, errText } from "../ui/useLoad";
import { ErrorNote, Loading } from "../ui/bits";
import { fmtAmount } from "../lib/money";
import { fmtDate } from "../companies/CompanyPicker";
import { STATES } from "../lib/india";
import { passwordProblem } from "./password";
import { Footer } from "../shell/Footer";

// YA10: your Altekore account — password, two-step sign-in, signed-in devices, your plan and Altekore's invoices,
// and companies you deleted (restore within 30 days)
export function AccountPage() {
  const back = (() => { try { return sessionStorage.getItem("books.lastCompanyName"); } catch { return null; } })();
  const [chosen, setChosen] = useState<string | null>(null);
  const [manageable, setManageable] = useState(false);
  // a link to one part (#password, #two-step …) scrolls to it once the sections have drawn
  useEffect(() => { const id = location.hash.slice(1); if (!id) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250); return () => clearTimeout(t); }, []);
  return (
    <div className="account-wrap">
    <main className="page account-page">
      <p className="small"><Link to="/">← {back ? `Back to ${back}` : "Back to Books"}</Link></p>
      {/* on Altekore's domains the profile, password and two-step sign-in live in the Altekore account (the menu opens it) */}
      <h1>{usesAccounts() ? "Plan and billing" : "Your profile and account"} <span className="code-tag">YA10</span></h1>
      {!usesAccounts() && <><Profile /><PasswordBox /><TwoStep /><Devices /></>}
      <PlanTiers onChoose={(code) => { setChosen(code); setTimeout(() => document.getElementById("billing")?.scrollIntoView({ behavior: "smooth" }), 50); }} manageable={setManageable} />
      {manageable && <Billing key={chosen ?? "-"} initialPlan={chosen} />}
      <Deleted />
    </main>
    <Footer />
    </div>
  );
}

// your name (shown on entries and in the edit log), your email, and signing out
function Profile() {
  const [user, setUser] = useState<{ email: string; name: string } | null>(null);
  const [name, setName] = useState(""); const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<string | null>(null); const [err, setErr] = useState<{ field: string; text: string } | null>(null);
  useEffect(() => { void sb.auth.getUser().then(({ data }) => {
    const u = { email: data.user?.email ?? "", name: String(data.user?.user_metadata?.full_name ?? "") };
    setUser(u); setName(u.name); setEmail(u.email);
  }); }, []);
  const saveName = async () => {
    setErr(null); setMsg(null);
    if (!name.trim()) { setErr({ field: "name", text: "Give your name: it's shown with the entries you make." }); return; }
    const { error } = await sb.auth.updateUser({ data: { full_name: name.trim() } });
    if (error) setErr({ field: "name", text: error.message }); else { setUser((u) => u && { ...u, name: name.trim() }); setMsg("Name saved."); }
  };
  const saveEmail = async () => {
    setErr(null); setMsg(null);
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setErr({ field: "email", text: "That isn't an email address." }); return; }
    const { error } = await sb.auth.updateUser({ email: e }, { emailRedirectTo: `${location.origin}/account` });
    if (error) setErr({ field: "email", text: error.message });
    else setMsg(`We've sent a confirmation link to ${e}. The change takes effect once you open it.`);
  };
  if (!user) return <section className="card pad"><Loading /></section>;
  const initials = (user.name || user.email).split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
  return (
    <section className="card pad" id="profile">
      <div className="profile-head"><span className="avatar large" aria-hidden="true">{initials}</span>
        <div className="grow"><h2>{user.name || "Your profile"}</h2><p className="muted small">{user.email}</p></div>
        <button onClick={() => void signOutEverywhere()}>Sign out</button></div>
      <div className="fields inline">
        <label>Your name<input value={name} maxLength={120} autoComplete="name" className={err?.field === "name" ? "invalid" : undefined} onChange={(e) => setName(e.target.value)} /></label>
        <button disabled={name.trim() === user.name} onClick={() => void saveName()}>Save name</button></div>
      <div className="fields inline">
        <label>Email<input type="email" value={email} autoComplete="email" className={err?.field === "email" ? "invalid" : undefined} onChange={(e) => setEmail(e.target.value)} /></label>
        <button disabled={email.trim().toLowerCase() === user.email} onClick={() => void saveEmail()}>Change email</button></div>
      {msg && <p className="msg ok">{msg}</p>}<ErrorNote text={err?.text ?? null} />
    </section>
  );
}

function PasswordBox() {
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState(""); const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<{ field: "pw" | "pw2"; text: string } | null>(null);
  const save = async () => {
    setErr(null); setMsg(null);
    const p = passwordProblem(pw); if (p) { setErr({ field: "pw", text: p }); return; }
    if (pw !== pw2) { setErr({ field: "pw2", text: "The two passwords don't match." }); return; }
    const { error } = await sb.auth.updateUser({ password: pw });
    if (error) setErr({ field: "pw", text: error.message }); else { setPw(""); setPw2(""); setMsg("Password changed. Other devices stay signed in; sign them out below if you need to."); }
  };
  return (
    <section className="card pad" id="password"><h2>Change password</h2>
      <div className="fields inline"><label>New password<input type="password" autoComplete="new-password" value={pw} className={err?.field === "pw" ? "invalid" : undefined} onChange={(e) => setPw(e.target.value)} /></label>
        <label>Type it again<input type="password" autoComplete="new-password" value={pw2} className={err?.field === "pw2" ? "invalid" : undefined} onChange={(e) => setPw2(e.target.value)} /></label>
        <button className="primary" disabled={!pw || !pw2} onClick={() => void save()}>Change password</button></div>
      <p className="muted small">At least 10 characters, with a letter and a digit, and not a common one.</p>
      {msg && <p className="msg ok">{msg}</p>}<ErrorNote text={err?.text ?? null} />
    </section>
  );
}

function TwoStep() {
  const [factors, setFactors] = useState<{ id: string; friendly_name?: string; status: string }[] | null>(null);
  const [enroll, setEnroll] = useState<{ id: string; qr: string; secret: string } | null>(null);
  const [code, setCode] = useState(""); const [err, setErr] = useState<string | null>(null); const [msg, setMsg] = useState<string | null>(null);
  const load = async () => { const { data } = await sb.auth.mfa.listFactors(); setFactors((data?.totp ?? []) as { id: string; friendly_name?: string; status: string }[]); };
  useEffect(() => { void load(); }, []);
  const start = async () => {
    setErr(null);
    const { data, error } = await sb.auth.mfa.enroll({ factorType: "totp", friendlyName: `Authenticator ${new Date().toISOString().slice(0, 10)}` });
    if (error) setErr(error.message); else setEnroll({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
  };
  const verify = async () => {
    if (!enroll) return;
    const { error } = await sb.auth.mfa.challengeAndVerify({ factorId: enroll.id, code: code.trim() });
    if (error) setErr(error.message); else { setEnroll(null); setCode(""); setMsg("Two-step sign-in is on. Next time you sign in, the app asks for a code."); void load(); }
  };
  const remove = async (id: string) => { const { error } = await sb.auth.mfa.unenroll({ factorId: id }); if (error) setErr(error.message); else void load(); };
  const verified = (factors ?? []).filter((f) => f.status === "verified");
  return (
    <section className="card pad" id="two-step"><h2>Two-step sign-in</h2>
      <p className="muted small">A code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password …) as well as your password. A company can require it of everyone who uses it.</p>
      {factors === null ? <Loading /> : verified.length > 0 ? <ul>{verified.map((f) => <li key={f.id}>{f.friendly_name ?? "Authenticator"} <button className="link danger-link" onClick={() => void remove(f.id)}>Turn off</button></li>)}</ul>
        : !enroll && <button className="primary" onClick={() => void start()}>Set up two-step sign-in</button>}
      {enroll && <div className="mfa-enroll">
        <img src={enroll.qr} alt="QR code for the authenticator app" width={180} height={180} />
        <p className="small">Scan it with the app, or type the key: <code>{enroll.secret}</code></p>
        <div className="fields inline"><label>The 6-digit code<input inputMode="numeric" autoComplete="one-time-code" className="short" value={code} onChange={(e) => setCode(e.target.value)} /></label>
          <button className="primary" disabled={code.trim().length < 6} onClick={() => void verify()}>Turn on</button></div>
      </div>}
      {msg && <p className="msg ok">{msg}</p>}<ErrorNote text={err} />
    </section>
  );
}

function Devices() {
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <section className="card pad" id="devices"><h2>Signed-in devices</h2>
      <p className="muted small">If a phone or computer was lost or shared, sign out everywhere else; this device stays signed in.</p>
      <div className="actions">
        <button onClick={() => void sb.auth.signOut({ scope: "others" }).then(({ error }) => setMsg(error ? error.message : "Signed out on every other device."))}>Sign out other devices</button>
        <button className="link danger-link" onClick={() => void sb.auth.signOut({ scope: "global" })}>Sign out everywhere, including here</button>
      </div>
      {msg && <p className="msg ok">{msg}</p>}
    </section>
  );
}

// the plans on offer for the company in use: which one it's on, and either a way to change it (for whoever manages its
// billing) or who to ask
function PlanTiers({ onChoose, manageable }: { onChoose: (code: string) => void; manageable: (on: boolean) => void }) {
  const company = lastCompany.get();
  const name = (() => { try { return sessionStorage.getItem("books.lastCompanyName"); } catch { return null; } })() ?? "This company";
  const { data, error } = useLoad(() => (company ? accountApi.companyPlan(company) : Promise.resolve(null)), [company]);
  useEffect(() => { manageable(!!data?.can_manage && !!data?.on); }, [data]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!company) return null;
  if (error) return <section className="card pad"><h2>Plans</h2><ErrorNote text={error.message} /></section>;
  if (!data) return <section className="card pad"><Loading /></section>;
  const current = data.plans.find((p) => p.code === data.plan);
  const rank = current?.rank ?? -1;
  const status = data.status === "trial" ? (data.trial_ends_at ? `free trial until ${fmtDate(data.trial_ends_at.slice(0, 10))}` : "free trial")
    : data.status === "active" ? (data.current_end ? `paid up to ${fmtDate(data.current_end.slice(0, 10))}` : "active") : data.status.replace("_", " ");
  return (
    <section className="card pad plan-tiers">
      <h2>Plans</h2>
      <p className="muted"><b className="ink">{name}</b> {data.status === "trial"
        ? <>is on the <b className="ink">free trial</b>{data.trial_ends_at ? `, until ${fmtDate(data.trial_ends_at.slice(0, 10))}` : ""}.</>
        : <>is on <b className="ink">{data.plan_name}</b>, {status}.</>}</p>
      {!data.can_manage && <p className="notice small">Billing for {name} is managed by <b>{data.managed_by ?? "the company's owner"}</b>{data.by_firm ? " (the firm that looks after its books)" : ""}. To change the plan, reach out to them or to your company admin.</p>}
      {!data.on && <p className="muted small">Payments aren't switched on yet, so plans can't be bought here today; they're shown for reference.</p>}
      <div className="tiers">{data.plans.map((p) => {
        const here = p.code === data.plan;
        const up = p.rank > rank;
        return (
          <div key={p.code} className={`tier${here ? " current" : ""}`}>
            <div className="tier-head"><b>{p.name}</b>{here && <span className="tag">Current plan</span>}</div>
            <div className="tier-price"><span className="amt">₹{fmtAmount(p.monthly).replace(/\.00$/, "")}</span><span className="muted small">{p.per_company ? "a month per company" : "a month"} + GST</span></div>
            <p className="muted small">{p.note}</p>
            <ul className="small">
              <li>{p.companies ? `Up to ${p.companies} compan${p.companies === 1 ? "y" : "ies"}` : "Any number of companies"}</li>
              <li>{p.users ? `${p.users} people` : "Any number of people"}</li>
              <li>Yearly: ₹{fmtAmount(p.yearly).replace(/\.00$/, "")}{p.per_company ? " per company" : ""} (two months free)</li>
            </ul>
            {!here && <button className={up ? "primary" : undefined} disabled={!data.can_manage || !data.on} onClick={() => onChoose(p.code)}
              title={!data.can_manage ? `Billing is managed by ${data.managed_by ?? "the company's owner"}` : !data.on ? "Payments aren't switched on yet" : undefined}>
              {up ? `Upgrade to ${p.name}` : `Switch to ${p.name}`}</button>}
          </div>);
      })}</div>
    </section>
  );
}

function Billing({ initialPlan }: { initialPlan?: string | null }) {
  const [rev, setRev] = useState(0);
  const { data, error } = useLoad(() => (rev ? accountApi.refresh() : accountApi.billing()), [rev]);
  const [f, setF] = useState({ plan: initialPlan ?? "solo", period: "monthly", billing_name: "", gstin: "", state_code: "", address: "" });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const [inv, setInv] = useState<BillingInvoice | null>(null);
  if (error) return <section className="card pad" id="billing"><h2>Plan and billing</h2><ErrorNote text={error.message} /></section>;
  if (!data) return null;
  if (!data.on) return <section className="card pad" id="billing"><h2>Plan and billing</h2><p className="muted small">Plans aren't switched on for this installation.</p></section>;
  const s = data.subscription;
  const price = (p: Plan) => `${fmtAmount(f.period === "yearly" ? p.yearly : p.monthly)} ${f.period === "yearly" ? "a year" : "a month"}${p.per_company ? " per company" : ""} + GST`;
  const subscribe = () => { setBusy(true); setErr(null);
    accountApi.subscribe(f).then((x) => { if (x.checkout_url) window.open(x.checkout_url, "_blank", "noopener"); setRev((r) => r + 1); })
      .catch((e) => setErr(errText(e))).finally(() => setBusy(false)); };
  return (
    <section className="card pad" id="billing"><h2>Plan and billing</h2>
      {s && <p>{s.plan_name} · <b>{s.status === "trial" ? `trial until ${fmtDate(s.trial_ends_at!.slice(0, 10))}` : s.status}</b>
        {s.current_end && s.status !== "trial" ? ` · paid up to ${fmtDate(s.current_end.slice(0, 10))}` : ""} · {s.companies} compan{s.companies === 1 ? "y" : "ies"}
        {s.lapsed && <span className="warn"> — lapsed: companies are read-only until you choose a plan</span>}</p>}
      {s?.status === "pending" && s.checkout_url && <p className="notice small">Finish paying on Razorpay: <a href={s.checkout_url} target="_blank" rel="noreferrer">open the payment page</a>, then <button className="link" onClick={() => setRev((r) => r + 1)}>check again</button>.</p>}
      <div className="plans">{data.plans.map((p) => <label key={p.code} className={`plan card ${f.plan === p.code ? "on" : ""}`}>
        <input type="radio" name="plan" checked={f.plan === p.code} onChange={() => setF({ ...f, plan: p.code })} />
        <b>{p.name}</b><span className="small">{price(p)}</span><span className="muted small">{p.note}{p.companies ? ` · up to ${p.companies} compan${p.companies === 1 ? "y" : "ies"}` : ""}{p.users ? ` · ${p.users} people` : ""}</span>
      </label>)}</div>
      <div className="fields two">
        <label>Pay<select value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })}><option value="monthly">Monthly</option><option value="yearly">Yearly (two months free)</option></select></label>
        <label>Name on the invoice<input value={f.billing_name} onChange={(e) => setF({ ...f, billing_name: e.target.value })} /></label>
        <label>GSTIN <span className="muted small">(for input credit)</span><input className="mono" value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} /></label>
        {!f.gstin && <label>State<select value={f.state_code} onChange={(e) => setF({ ...f, state_code: e.target.value })}><option value="">—</option>{STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}</select></label>}
        <label>Address<input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></label>
      </div>
      <div className="actions"><button className="primary" disabled={busy} onClick={subscribe}>{s && s.status === "active" ? "Change plan" : "Subscribe"}</button>
        {data.mode === "test" && <span className="muted small">Test mode: no money moves.</span>}</div>
      <ErrorNote text={err} />
      {s && s.invoices.length > 0 && <><h3>Invoices from Altekore</h3>
        <table className="grid-table"><tbody>{s.invoices.map((i) => <tr key={i.id}><td>{i.number}</td><td>{fmtDate(i.issued_on)}</td><td>{fmtDate(i.period_from)} – {fmtDate(i.period_to)}</td>
          <td className="num">{fmtAmount(i.total)}</td><td><button className="link" onClick={() => void accountApi.invoice(i.id).then(setInv)}>View</button></td></tr>)}</tbody></table></>}
      {inv && <div className="overlay" onMouseDown={() => setInv(null)}><div className="dialog card billing-invoice" onMouseDown={(e) => e.stopPropagation()}>
        <h3>Tax invoice {inv.number}</h3>
        <p className="small"><b>{inv.customer.seller.name}</b>{inv.customer.seller.gstin && ` · GSTIN ${inv.customer.seller.gstin}`}<br />{inv.customer.seller.address}</p>
        <p className="small">To <b>{inv.customer.name}</b>{inv.customer.gstin && ` · GSTIN ${inv.customer.gstin}`}<br />{inv.customer.address}</p>
        <table className="grid-table"><tbody>
          <tr><td>{inv.description} (SAC {inv.customer.seller.sac}), {fmtDate(inv.period_from)} – {fmtDate(inv.period_to)}</td><td className="num">{fmtAmount(inv.taxable)}</td></tr>
          {inv.igst > 0 && <tr><td>IGST 18%</td><td className="num">{fmtAmount(inv.igst)}</td></tr>}
          {inv.cgst > 0 && <><tr><td>CGST 9%</td><td className="num">{fmtAmount(inv.cgst)}</td></tr><tr><td>SGST 9%</td><td className="num">{fmtAmount(inv.sgst)}</td></tr></>}
          <tr className="total-row"><td>Total</td><td className="num">{fmtAmount(inv.total)}</td></tr></tbody></table>
        <div className="actions"><button onClick={() => window.print()}>Print</button><button className="link" onClick={() => setInv(null)}>Close</button></div>
      </div></div>}
    </section>
  );
}

function Deleted() {
  const [rev, setRev] = useState(0);
  const { data } = useLoad(() => accountApi.deleted(), [rev]);
  const [err, setErr] = useState<string | null>(null);
  if (!data || data.length === 0) return null;
  return (
    <section className="card pad"><h2>Deleted companies</h2>
      <table className="grid-table"><tbody>{data.map((c) => <tr key={c.id}><td>{c.name}</td><td className="small">deleted {fmtDate(c.deleted_at.slice(0, 10))}{c.by ? ` by ${c.by}` : ""}; gone for good after {fmtDate(c.purge_after.slice(0, 10))}</td>
        <td><button onClick={() => accountApi.restore(c.id).then(() => setRev((r) => r + 1)).catch((e) => setErr(errText(e)))}>Restore</button></td></tr>)}</tbody></table>
      <ErrorNote text={err} />
    </section>
  );
}
